import React, { useEffect, useCallback } from 'react';
import { fetchCommentsStats } from '../../../../store/actions/SocialAction';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List } from '@mui/material';
import HasRight from '../../../Route/HasRight';
import ListItemNavLink from '../ListItemNavLink';
import {
	getSiteAddressRoute,
	getSiteFinancialReportRoute,
	getSiteSettingsRoute
} from '../../../../router/routes/site/factory/setting';
import SettingsIcon from '@mui/icons-material/Settings';
import { getSiteUsersRoute } from '../../../../router/routes/site/factory/user';
import { FaUsers } from 'react-icons/fa';
import {
	getSiteCommentsRoute,
	getSiteNewsRoute,
	getSiteNotificationListRoute,
	getSiteNotificationRoute
} from '../../../../router/routes/site/factory/content';
import { TiNews } from 'react-icons/ti';
import { getSiteOrdersRoute } from '../../../../router/routes/site/factory/order';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Badge from '@mui/material/Badge';
import CommentIcon from '@mui/icons-material/Comment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';

const MenuWebsite = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const commentsCount = useSelector(state => state.social.commentsStats);
	const selectedRealm = useSelector(state => state.realm.selected);
	const registryUrl = useSelector(state => state.registry.url);
	const newsCms = useSelector(state => state.cms.news_cms);
	const ready = useSelector(state => state.common.ready);
	const creatingArtist = useSelector(state => state.music.creating);
	const settings = useSelector(state => state.template.templateSettings);

	let showComments = true;
	if (settings?.Data?.social && !settings.Data.social.includes('comment')) {
		showComments = false;
	}

	const refreshComments = useCallback((realmId) => {
		dispatch(fetchCommentsStats(realmId));
	}, [dispatch]);

	useEffect(() => {
		if (!selectedRealm) return;
		refreshComments(selectedRealm.Realm__);
	}, [selectedRealm, refreshComments]);

	useEffect(() => {
		const refreshInterval = setInterval(() => {
			if (!selectedRealm || creatingArtist) return;
			refreshComments(selectedRealm.Realm__);
		}, 30000);

		return () => clearInterval(refreshInterval);
	}, [creatingArtist, selectedRealm, refreshComments]);

	return (
		<List>
			<HasRight manager hideLoading>
				<ListItemNavLink
					tooltip={t('menu_site_settings')}
					disabled={registryUrl === null || !ready}
					path={getSiteSettingsRoute()}
					icon={<SettingsIcon />}
					primary={t('menu_site_settings')}
				/>
			</HasRight>

			<ListItemNavLink
				tooltip={t('menu_users')}
				disabled={selectedRealm === null || !ready}
				path={getSiteUsersRoute()}
				icon={<FaUsers size={20} />}
				primary={t('menu_users')}
			/>

			<ListItemNavLink
				tooltip={t('menu_news')}
				disabled={selectedRealm === null || !newsCms || !ready}
				path={getSiteNewsRoute()}
				icon={<TiNews size={20} />}
				primary={t('menu_news')}
			/>

			<HasRight support hideLoading>
				<ListItemNavLink
					tooltip={t('menu_orders')}
					disabled={selectedRealm === null || !ready}
					path={getSiteOrdersRoute()}
					icon={<CreditCardIcon />}
					primary={t('menu_orders')}
				/>
			</HasRight>

			{showComments && (
				<ListItemNavLink
					tooltip={t('menu_comments')}
					disabled={selectedRealm === null || !ready}
					path={getSiteCommentsRoute()}
					icon={
						<Badge color="primary" badgeContent={parseInt(commentsCount) || 0}>
							<CommentIcon />
						</Badge>
					}
					primary={t('menu_comments')}
				/>
			)}

			<HasRight manager hideLoading>
				<ListItemNavLink
					exact
					tooltip={t('menu_site_notify')}
					disabled={!ready}
					path={getSiteNotificationRoute()}
					icon={<NotificationsActiveIcon />}
					primary={t('menu_site_notify')}
				/>
				<ListItemNavLink
					tooltip={t('menu_site_notify_list')}
					disabled={!ready}
					path={getSiteNotificationListRoute()}
					icon={<SpeakerNotesIcon />}
					primary={t('menu_site_notify_list')}
				/>
			</HasRight>

			<HasRight admin hideLoading>
				<ListItemNavLink
					tooltip={t('menu_site_financial_report')}
					disabled={registryUrl === null || !ready}
					path={getSiteFinancialReportRoute()}
					icon={<AccountBalanceIcon />}
					primary={t('menu_site_financial_report')}
				/>
			</HasRight>

			<HasRight owner hideLoading>
				<ListItemNavLink
					tooltip={t('menu_site_address')}
					disabled={registryUrl === null || !ready}
					path={getSiteAddressRoute()}
					icon={<BusinessIcon />}
					primary={t('menu_site_address')}
				/>
			</HasRight>
		</List>
	);
};

export default MenuWebsite;
